import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getProducts, deleteProduct } from "./api/labelStudioApi";

const ProductsList = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (err) {
      toast.error("Failed to load products");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteProduct(id);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete product");
    }
  };

  const filtered = products.filter((p) =>
    `${p.productNameEn} ${p.productNameMr} ${p.sku}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Products
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/label-studio/products/new")}>
            Add Product
          </Button>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ borderRadius: 3, overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>MRP</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Avatar variant="rounded" src={p.productImageUrl} sx={{ width: 40, height: 40 }}>
                    {p.productNameEn?.charAt(0)}
                  </Avatar>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600}>{p.productNameEn}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.productNameMr}
                  </Typography>
                </TableCell>
                <TableCell>{p.brandNameEn}</TableCell>
                <TableCell>{p.category?.nameEn || "-"}</TableCell>
                <TableCell>&#8377;{p.mrp ?? "-"}</TableCell>
                <TableCell>{p.sku}</TableCell>
                <TableCell>
                  {p.vegType && (
                    <Chip
                      size="small"
                      label={p.vegType === "VEG" ? "Veg" : "Non-Veg"}
                      color={p.vegType === "VEG" ? "success" : "error"}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => navigate(`/label-studio/products/${p.id}/edit`)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No products found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default ProductsList;
